<script setup lang="ts">
import { computed, ref } from 'vue';
import '@patsnap-ui/icon/assets/solid/tick.svg';

const props = withDefaults(defineProps<{
    name: string;
    isActive?: boolean;
    isSelected?: boolean;
    theme?: 'blue' | 'green' | 'purple' | 'default'
    width?: string;
    maxWidth?: string;
}>(), {
    theme: 'default',
    isActive: false,
    isSelected: false,
});

const width = computed(() => {
    return props.width ? `calc(${props.width} - 4px)` : 'auto';
});

const maxWidth = computed(() => {
    const maxWidth = props.maxWidth || props.width || '332px';
    return `calc(${maxWidth} - 4px)`;
});

const isHovering = ref(false);

const classList = computed(() => {
    return [
        'common-badge-text',
        `common-badge-text--${props.theme}`,
        {
            'common-badge-text--active': props.isActive || isHovering.value,
            'common-badge-text--selected': props.isSelected,
        },
    ];
});
</script>

<template>
    <div
        :class="classList"
        :style="{ 'width': width, 'max-width': maxWidth }"
        @mouseenter="isHovering = true"
        @mouseleave="isHovering = false"
    >
        <span class="common-badge-text__name" :title="props.name">{{ props.name }}</span>
        <span v-if="props.isSelected" class="common-badge-text__selected-tag">
            <svg width="12" height="12" class="th-btn-icon fill-white">
                <use xlink:href="#SolidTick" />
            </svg>
        </span>
    </div>
</template>

<style lang="scss">
.common-badge-text {
    @apply px-[7px] py-[3px] rounded bg-gray-30 inline-flex items-center cursor-pointer text-[14px] leading-5 border border-gray-30 relative overflow-hidden;
    .common-badge-text__name {
        @apply text-black flex-shrink-0 overflow-hidden text-ellipsis w-full whitespace-nowrap;
    }
    .common-badge-text__selected-tag {
        @apply absolute left-[-10px] top-[-10px] rotate-45 w-5 h-5;
        svg {
            @apply rotate-[-45deg] absolute right-[-2px] bottom-[5px] ;
        }
    }

    &.common-badge-text--active {
        .common-badge-text__name {
            @apply font-normal;
        }
    }

    &.common-badge-text--green.common-badge-text--active,
    &.common-badge-text--green.common-badge-text--selected {
        @apply bg-green-75 border-green-75;
        .common-badge-text__name {
            @apply text-green-500;
        }
    }

    &.common-badge-text--green.common-badge-text--selected {
        @apply border-green-500;
        .common-badge-text__selected-tag {
            @apply bg-green-500;
        }
    }

    &.common-badge-text--purple.common-badge-text--active,
    &.common-badge-text--purple.common-badge-text--selected {
        @apply bg-[#F3EBF9] border-[#F3EBF9];
        .common-badge-text__name {
            @apply text-purple-500;
        }
    }

    &.common-badge-text--purple.common-badge-text--selected {
        @apply border-purple-500;
        .common-badge-text__selected-tag {
            @apply bg-purple-500;
        }
    }

    &.common-badge-text--blue.common-badge-text--active,
    &.common-badge-text--blue.common-badge-text--selected {
        @apply bg-blue-75 border-blue-75;
        .common-badge-text__name {
            @apply text-primary-blue-hover;
        }
    }

    &.common-badge-text--blue.common-badge-text--selected {
        @apply border-primary-blue-hover;
        .common-badge-text__selected-tag {
            @apply bg-primary-blue-hover;
        }
    }
}
</style>
